import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Order } from '@alpha-edge/shared'
import { PageHeader, DataTable, StatusBadge, ColumnSettingsButton, colors, btnPrimary } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { AccessioningModal } from './AccessioningModal'
import { useTableColumns } from '../../hooks/use-table-columns'
import { invalidateAccessionCount } from '../../hooks/use-accession-count'

interface AccessionResult {
  readonly id: string
  readonly accessionNumber: string
  readonly barcode: string
  readonly barcodeFormat: string
  readonly status: string
  readonly accessionedAt: string | null
}

function formatWait(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 60) return `${min}m`
  const hrs = Math.floor(min / 60)
  return `${hrs}h ${min % 60}m`
}

export function AccessioningPage() {
  const navigate = useNavigate()
  const [pendingOrders, setPendingOrders] = useState<readonly Order[]>([])
  const [recentlyAccessioned, setRecentlyAccessioned] = useState<readonly AccessionResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('accessioning')

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly Order[]>('/orders?status=submitted&limit=100')
      .then((res) => {
        if (cancelled) return
        setPendingOrders(res.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load orders')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  function openAccessionModal(order: Order) {
    setSelectedOrder(order)
    setModalOpen(true)
  }

  function handleAccessioned(result: AccessionResult) {
    setRecentlyAccessioned((prev) => [result, ...prev])
    setPendingOrders((prev) => prev.filter((o) => o.id !== result.id))
    invalidateAccessionCount()
  }

  const pendingColumns: readonly Column<Order>[] = [
    {
      key: 'orderNumber',
      header: 'Order #',
      sortable: true,
      render: (o) => <strong>{o.orderNumber || o.id.slice(0, 8)}</strong>,
    },
    { key: 'tests', header: 'Tests', filterable: true, render: (o) => o.testTypes.join(', ') },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (o) => <StatusBadge status={o.priority} />,
    },
    {
      key: 'provider',
      header: 'Provider',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      render: (o) => o.orderingProviderName || '--',
    },
    {
      key: 'patientLastName',
      header: 'Patient Last',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      render: (o) => o.patientLastName || '--',
    },
    {
      key: 'patientFirstName',
      header: 'Patient First',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      render: (o) => o.patientFirstName || '--',
    },
    {
      key: 'age',
      header: 'Waiting',
      sortable: true,
      render: (o) => formatWait(o.createdAt),
      sortValue: (o) => new Date(o.createdAt).getTime(),
    },
    {
      key: 'action',
      header: '',
      width: '120px',
      hideable: false,
      render: (o) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            openAccessionModal(o)
          }}
          style={{ ...btnPrimary, padding: '0.3rem 0.75rem', fontSize: '0.8125rem' }}
        >
          Accession
        </button>
      ),
    },
  ]

  const recentColumns: readonly Column<AccessionResult>[] = [
    { key: 'accessionNumber', header: 'Accession #', render: (r) => <strong>{r.accessionNumber}</strong> },
    {
      key: 'barcode',
      header: 'Barcode',
      render: (r) => (
        <span style={{ fontFamily: 'monospace', fontSize: '0.9375rem', letterSpacing: '1px' }}>{r.barcode}</span>
      ),
    },
    { key: 'format', header: 'Format', render: (r) => r.barcodeFormat },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ]

  return (
    <div>
      <PageHeader
        title="Accession"
        subtitle={`${pendingOrders.length} orders awaiting accessioning`}
        actions={
          <>
            <ColumnSettingsButton columns={pendingColumns} {...tableColumnProps} />
            <button type="button" onClick={() => navigate('/orders/new')} style={btnPrimary}>
              New Order
            </button>
          </>
        }
      />

      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <h2 style={{ fontSize: '1rem', fontWeight: 600, color: colors.textHeading, margin: '0 0 0.5rem' }}>
        Pending Orders
      </h2>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={pendingColumns}
          rows={pendingOrders}
          keyField="id"
          onRowClick={openAccessionModal}
          emptyMessage="No orders awaiting accessioning. All caught up."
          {...tableColumnProps}
        />
      )}

      {recentlyAccessioned.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, color: colors.textHeading, margin: '0 0 0.5rem' }}>
            Recently Accessioned
          </h2>
          <DataTable columns={recentColumns} rows={recentlyAccessioned} keyField="id" emptyMessage="" />
        </div>
      )}

      <AccessioningModal
        order={selectedOrder}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onAccessioned={handleAccessioned}
      />
    </div>
  )
}
